// ProblemView.tsx
import React, { useEffect, useState } from 'react';
import { Problem, ProblemStatus } from '../../models/Problem';
import { useProblem } from '../../hooks/useProblem';
import { useSubtasks } from '../../hooks/useSubtasks';
import { useRollen } from '../../hooks/useRollen';
import { StyledViewContainer } from './ProblemStyles';
import ProblemHeader from './ProblemViewComponents/ProblemHeader';
import ProblemDescription from './ProblemViewComponents/ProblemDescription';
import ProblemActions from './ProblemViewComponents/ProblemActions';
import SubtasksSection from './ProblemViewComponents/SubtasksSection';
import RollenSection from './ProblemViewComponents/RollenSection';
import SolutionSection from './ProblemViewComponents/SolutionSection';
import ProgressInfoBar from './ProblemViewComponents/ProgressInfoBar';
import { BreakdownProblem, ProcessTasks, ConsolidateAndGenerateFinal } from '../../services/api';
import { Snackbar, Alert } from '@mui/material';
import { db } from '../../services/firebaseConfig';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { SubtaskStatus } from '../../models/Subtask';
import { ExpertStatus } from '../../models/Rollen';

interface ProblemViewProps {
  problem: Problem;
  onClose: () => void;
}

const ProblemView: React.FC<ProblemViewProps> = ({ problem, onClose }) => {
  const { updateProblemStatus, deleteProblem, problems } = useProblem();
  const {
    subtasks,
    loading: subtasksLoading,
    error: subtasksError,
    generatingSubtasks,
    updateStatus,
  } = useSubtasks(problem.id, problem.userId);

  const {
    rollen,
    loading: rollenLoading,
    error: rollenError,
    updateStatus: updateRollenStatus,
  } = useRollen(problem.id, problem.userId);

  const [problemStatus, setProblemStatus] = useState<ProblemStatus>(problem.status);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [logs, setLogs] = useState<Array<any>>([]);

  // Synchronize local status state with the Redux state
  useEffect(() => {
    const updatedProblem = problems.find(p => p.id === problem.id);
    if (updatedProblem) {
      setProblemStatus(updatedProblem.status);
    }
  }, [problems, problem.id]);

  // Listen to logs from Firestore
  useEffect(() => {
    const userId = problem.userId;
    const problemId = problem.id;

    const logsRef = collection(db, 'logs', userId, 'problems', problemId, 'log_entries');
    const q = query(logsRef, orderBy('timestamp'));

    const unsubscribe = onSnapshot(q, querySnapshot => {
      const logsData: Array<any> = [];
      querySnapshot.forEach(doc => {
        logsData.push(doc.data());
      });
      setLogs(logsData);
    });

    return () => unsubscribe();
  }, [problem.userId, problem.id]);

  const handleStatusChange = () => {
    const newStatus: ProblemStatus = problemStatus === 'open' ? 'closed' : 'open';
    updateProblemStatus(problem.id, newStatus);
  };

  const handleDelete = () => {
    deleteProblem(problem.id);
    onClose();
  };

  const handleSolve = async () => {
    try {
      // Set status to in_progress before making the API call
      await updateProblemStatus(problem.id, 'in_progress');

      console.log('Initiating problem solving for:', problem);
      await BreakdownProblem({
        problemId: problem.id,
        userId: problem.userId,
      });

      // Start processing tasks after breakdown is complete
      console.log('Starting task processing for:', problem);
      await ProcessTasks({
        problemId: problem.id,
        userId: problem.userId,
      });

      // Consolidate results and generate final document
      console.log('Consolidating results and generating final document:', problem);
      await ConsolidateAndGenerateFinal({
        problemId: problem.id,
        userId: problem.userId,
      });

      await updateProblemStatus(problem.id, 'completed');
    } catch (error) {
      console.error('Error solving problem:', error);
      setErrorMessage('An error occurred while solving the problem.');
      // Revert status if solving fails
      await updateProblemStatus(problem.id, 'open');
    }
  };

  const handleSubtaskStatusChange = async (subtaskId: string, newStatus: SubtaskStatus) => {
    try {
      await updateStatus(subtaskId, newStatus);
    } catch (error) {
      setErrorMessage('Failed to update subtask status');
    }
  };

  const handleRolleStatusChange = async (rolleId: string, newStatus: ExpertStatus) => {
    try {
      await updateRollenStatus(rolleId, newStatus);
    } catch (error) {
      setErrorMessage('Failed to update expert status');
    }
  };

  // Get the last log message and parse it
  const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;
  const logMessage = lastLog ? lastLog.message : '';
  const parsedMessage = logMessage.split('global_logger - INFO - ')[1] || logMessage;

  const isCurrentlyProcessing = problemStatus === 'in_progress' || generatingSubtasks;

  return (
    <StyledViewContainer>
      <ProblemHeader title={problem.title} onClose={onClose} />

      <ProblemDescription description={problem.description} status={problemStatus} />
      <ProblemActions
        status={problemStatus}
        isProcessing={isCurrentlyProcessing}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        onSolve={handleSolve}
      />
      <ProgressInfoBar
        show={problemStatus === 'in_progress'}
        isProcessing={isCurrentlyProcessing}
        message={parsedMessage}
      />
      <SubtasksSection
        subtasks={subtasks}
        loading={subtasksLoading}
        problemId={problem.id}
        userId={problem.userId}
        onStatusChange={handleSubtaskStatusChange}
      />

      <RollenSection
        rollen={rollen}
        loading={rollenLoading}
        problemId={problem.id}
        userId={problem.userId}
        onStatusChange={handleRolleStatusChange}
      />

      <SolutionSection
        problemId={problem.id}
        userId={problem.userId}
        show={problemStatus === 'completed'}
      />

      {/* Error Messages */}
      <Snackbar
        open={!!errorMessage || !!subtasksError || !!rollenError}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage || subtasksError || rollenError}
        </Alert>
      </Snackbar>
    </StyledViewContainer>
  );
};

export default ProblemView;
